import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { GraduationCap } from 'lucide-react';
import { fmt, SABadge, Spinner, ErrorBanner, PaginationBar, UNAVAILABLE } from '../components/ReportTableKit';

const API = process.env.REACT_APP_BACKEND_URL;

export default function InsightsCubeSkillingHub() {
  const [searchInput, setSearchInput] = useState('');
  const [applied, setApplied] = useState('');
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, last_page: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const PER = 100;

  const fetchData = useCallback(async (p, search) => {
    setLoading(true); setErr(null);
    try {
      const params = { page: p, per_page: PER };
      if (search) params.search = search;
      const r = await axios.get(`${API}/api/lup/v2/report/skilling-hub-courses`, { params, withCredentials: true });
      setRows(r.data?.data || []);
      setMeta({ total: r.data?.total || 0, last_page: r.data?.last_page || 1 });
      setPage(r.data?.current_page || p);
    } catch (e) {
      setErr(UNAVAILABLE('Skilling Hub course')); setRows([]); setMeta({ total: 0, last_page: 1 });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(1, applied); }, [fetchData, applied]);

  const apply = () => setApplied(searchInput);
  const reset = () => { setSearchInput(''); setApplied(''); };

  return (
    <div data-testid="insights-skilling-hub-page">
      <div className="page-header">
        <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}><GraduationCap size={22} color="#4338ca" /> Courses for PSH</h1>
        <p>Courses feed delivered to EAS Partner Skilling Hub (PSH) — the Skilling Hub course list, sourced live from the LevelUp reporting API.</p>
      </div>
      <div className="filter-bar" style={{ alignItems: 'flex-end' }}>
        <div className="filter-group"><label className="filter-label">Search</label>
          <input type="text" className="filter-input" placeholder="Course, code, category, level, tags…" value={searchInput} onChange={e => setSearchInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && apply()} data-testid="skilling-hub-search" style={{ minWidth: 280 }} /></div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" onClick={apply} data-testid="skilling-hub-apply-btn">Apply</button>
          <button className="btn btn-secondary" onClick={reset} data-testid="skilling-hub-reset-btn">Reset</button></div>
      </div>
      <div className="table-container">
        {loading ? <Spinner /> : err ? <ErrorBanner message={err} testId="skilling-hub-error" /> : rows.length === 0 ?
          <div className="empty-state"><h3>No courses found</h3><p>Try a different search.</p></div> : (<>
            <div className="table-header"><span className="table-title">PSH Courses Feed</span><span className="table-count" style={{ marginLeft: 8 }}>({fmt(meta.total)} rows)</span></div>
            <div className="table-scroll"><table data-testid="skilling-hub-table"><thead><tr>
              <th>Course ID</th><th>Name</th><th>Course Code</th><th>Category</th><th>Level</th><th>Audience</th><th>Language</th><th>Duration</th><th>Tags</th>
            </tr></thead><tbody>{rows.map((row, i) => <tr key={i} data-testid={`skilling-hub-row-${i}`}>
              <td style={{ fontSize: 12, fontFamily: 'monospace' }}>{row.course_id ?? '—'}</td>
              <td style={{ fontSize: 13, fontWeight: 500, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.name}>{row.name || '—'}</td>
              <td style={{ fontSize: 12, fontFamily: 'monospace' }}>{row.course_code || '—'}</td>
              <td><SABadge name={row.catalog_category} /></td>
              <td style={{ fontSize: 12 }}>{row.level || '—'}</td>
              <td style={{ fontSize: 12 }}>{row.targeted_audience || '—'}</td>
              <td style={{ fontSize: 12 }}>{row.language || '—'}</td>
              <td style={{ fontSize: 12 }}>{row.course_duration || '—'}</td>
              <td style={{ fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.tags}>{row.tags || '—'}</td>
            </tr>)}</tbody></table></div>
            <PaginationBar page={page} totalPages={meta.last_page} total={meta.total} perPage={PER} onPage={p => fetchData(p, applied)} testId="skilling-hub-pagination" />
          </>)}
      </div>
    </div>
  );
}
